/**
 * AdminNotificationJobs.js — admin panelde zamanlanmış bildirim görevlerini
 * (şu an sadece daily_login_reminder) açıp kapatan sade sayfa.
 *
 * Route: /admin/notification-jobs (system nav grubu — announcements'ın hemen
 * altı; şu an ayrı bir "Ayarlar" sayfası olmadığı için en yakın IA yuvası).
 *
 * API (bkz. backend/routes/notifications.py — sadece backend-dev'in eklediği
 * gerçek şema, uydurulmadı):
 *   GET  /admin/notification-jobs             → { jobs: [{ job_key, label_tr,
 *        label_en, description_tr, description_en, enabled, last_run }] }
 *   PUT  /admin/notification-jobs/{job_key}    body { enabled } → { job: {...} }
 *
 * NOT: Registry'de şu an SADECE daily_login_reminder var (study_inactivity /
 * interview_availability job'larının gerçek bir flag'i yok — backend bilerek
 * onları listelemiyor). Liste GET'ten gelen diziden render edilir, hiçbir job
 * sabit kodlanmaz — backend ileride genişletince arayüz kendiliğinden büyür.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, RefreshCw, Info } from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { PageHeader, EmptyState, LoadingState } from '../components/admin';
import { Switch } from '../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso, lang) {
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  try {
    return dt.toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US');
  } catch {
    return null;
  }
}

function JobCard({ job, lang, t, onToggle, toggling }) {
  const label = lang === 'tr' ? job.label_tr : job.label_en;
  const description = lang === 'tr' ? job.description_tr : job.description_en;
  const lastRunDate = fmtDate(job.last_run?.run_at, lang);

  return (
    <div className="card-clay p-4 flex flex-col gap-3" data-testid={`notif-job-${job.job_key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${job.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
            <Bell size={18} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-sm text-[var(--text-main)]">{label}</p>
            {description && (
              <p className="text-xs text-[var(--text-muted)] mt-0.5">{description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold ${job.enabled ? 'text-emerald-600' : 'text-gray-400'}`}>
            {job.enabled ? t('admin.notifJobs.toggleOn') : t('admin.notifJobs.toggleOff')}
          </span>
          <Switch
            checked={job.enabled}
            disabled={toggling}
            onCheckedChange={(next) => onToggle(job.job_key, next)}
            aria-label={label}
            data-testid={`notif-job-toggle-${job.job_key}`}
          />
        </div>
      </div>

      <div className="border-t border-[var(--border-light)] pt-2 text-xs text-[var(--text-muted)]">
        <span className="font-semibold">{t('admin.notifJobs.lastRun')}: </span>
        {job.last_run ? (
          <span>
            {lastRunDate || job.last_run.run_at}
            {job.last_run.status === 'disabled' && (
              <span className="ml-1 text-amber-600 font-semibold">
                ({t('admin.notifJobs.statusDisabled')})
              </span>
            )}
            {typeof job.last_run.notified === 'number' && typeof job.last_run.checked === 'number' && (
              <span className="ml-1">
                — {t('admin.notifJobs.notifiedOf')
                  .replace('{{notified}}', job.last_run.notified)
                  .replace('{{checked}}', job.last_run.checked)}
              </span>
            )}
          </span>
        ) : (
          <span>{t('admin.notifJobs.lastRunNever')}</span>
        )}
      </div>
    </div>
  );
}

export default function AdminNotificationJobs() {
  const { lang, t } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await axios.get(`${API}/admin/notification-jobs`, { headers: authHeaders() });
      setJobs(r.data.jobs || []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleToggle = async (jobKey, nextEnabled) => {
    setTogglingKey(jobKey);
    // İyimser güncelleme — geri alınır eğer istek başarısız olursa.
    setJobs((prev) => prev.map((j) => (j.job_key === jobKey ? { ...j, enabled: nextEnabled } : j)));
    try {
      const r = await axios.put(
        `${API}/admin/notification-jobs/${jobKey}`,
        { enabled: nextEnabled },
        { headers: authHeaders() }
      );
      setJobs((prev) => prev.map((j) => (j.job_key === jobKey ? r.data.job : j)));
      toast.success(t('admin.notifJobs.toggleSuccess'));
    } catch (err) {
      setJobs((prev) => prev.map((j) => (j.job_key === jobKey ? { ...j, enabled: !nextEnabled } : j)));
      toast.error(getApiErrorMessage(err, lang) || t('admin.notifJobs.toggleError'));
    } finally {
      setTogglingKey(null);
    }
  };

  return (
    <AdminLayout>
      <PageHeader
        title={t('admin.notifJobs.title')}
        subtitle={t('admin.notifJobs.subtitle')}
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('admin.notifJobs.title') },
        ]}
      />

      {loading && <LoadingState variant="cards" count={2} />}

      {!loading && error && (
        <EmptyState
          icon={<Bell size={40} strokeWidth={1.5} />}
          title={t('admin.notifJobs.loadError')}
          action={{ label: t('admin.notifJobs.retry'), onClick: fetchJobs }}
        />
      )}

      {!loading && !error && jobs.length === 0 && (
        <EmptyState
          icon={<Bell size={40} strokeWidth={1.5} />}
          title={t('admin.notifJobs.emptyTitle')}
          description={t('admin.notifJobs.emptyDesc')}
        />
      )}

      {!loading && !error && jobs.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobs.map((job) => (
              <JobCard
                key={job.job_key}
                job={job}
                lang={lang}
                t={t}
                onToggle={handleToggle}
                toggling={togglingKey === job.job_key}
              />
            ))}
          </div>

          <div className="mt-4 flex items-start gap-2 text-xs text-[var(--text-muted)] bg-sky-50 border-2 border-sky-100 rounded-xl p-3">
            <Info size={14} className="shrink-0 mt-0.5 text-sky-500" />
            <span>{t('admin.notifJobs.infoNote')}</span>
          </div>

          <button
            type="button"
            onClick={fetchJobs}
            disabled={loading}
            className="mt-4 flex items-center gap-1.5 text-xs font-bold text-[var(--brand-primary)] hover:underline disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            {t('admin.notifJobs.retry')}
          </button>
        </>
      )}
    </AdminLayout>
  );
}
